import {groupBy, uniqBy} from "lodash";
import {Box} from "@material-ui/core";
import clsx from "clsx";
import ExtraProductItem from "view/components/ShoppingCart/components/ExtraProductItem";
import FeeItem from "view/components/ShoppingCart/components/FeeItem";
import React from "react";
import {FullProduct} from "ts/api/product";
import {LinkedPropertyResult} from "tools/product/productLinkedProperties";
import {useQuickOrderId} from "hooks/show/order";
import ProductItem, {ProductItemProps} from "./components/ProductItem";
import useStyles from "./styles";

export type ShoppingCartEventProps = {
  onRemoveProduct: (id: string) => void;
  onChangeQuantity: (id: string, quantity: number) => void;
  onChangeExtraProductQuantity: (
    id: string,
    parentProductId: string,
    quantity: number
  ) => void;
  onRemoveExtraProduct: (id: string, parentProductId: string) => void;
  onNoteChange: (productId: string, note: string) => void;
  linkedPropertyByProductId: Record<string, LinkedPropertyResult>;
};

type ProductsProps = Pick<ProductItemProps, "onAttachmentModalOpen"> &
  ShoppingCartEventProps & {
    products: FullProduct[];
  };

function Products({
  products,
  onRemoveProduct,
  onAttachmentModalOpen,
  onChangeQuantity,
  onNoteChange,
  onRemoveExtraProduct,
  onChangeExtraProductQuantity,
  linkedPropertyByProductId,
}: ProductsProps) {
  const quickOrderId = useQuickOrderId();
  const classes = useStyles();
  const groupBySupplierCompany = Object.values(groupBy(products, "owner.id"));
  const groupedProducts = groupBySupplierCompany.map((groupedProducts, idx) => {
    const supplierCompanyFees =
      groupedProducts?.length > 1
        ? uniqBy(groupedProducts.map((product) => product.fees).flat(1), "id")
        : null;
    const isLast = groupBySupplierCompany.length - 1 === idx;

    return (
      <Box
        key={idx}
        className={clsx(
          classes.supplerProductsContainer,
          isLast && classes.lastSupplerProductsContainer
        )}
      >
        {groupedProducts.map((product, idx) => (
          <Box
            key={quickOrderId ? idx : product.id}
            className={classes.productContainer}
          >
            <ProductItem
              product={product}
              onNoteChange={onNoteChange}
              onChangeQuantity={onChangeQuantity}
              onRemoveProduct={onRemoveProduct}
              onAttachmentModalOpen={onAttachmentModalOpen}
              showFees={groupedProducts.length === 1}
              linkedProperty={linkedPropertyByProductId[product.id]}
              idx={idx}
            />
            {product.extras?.map((extraProduct) => (
              <ExtraProductItem
                key={extraProduct.id}
                product={extraProduct}
                onChangeQuantity={(id, quantity) =>
                  onChangeExtraProductQuantity(id, product.id, quantity)
                }
                onRemoveProduct={(id) => onRemoveExtraProduct(id, product.id)}
              />
            ))}
          </Box>
        ))}
        {supplierCompanyFees?.length
          ? supplierCompanyFees?.map((fee) => (
              <FeeItem key={fee.id} fee={fee} />
            ))
          : null}
      </Box>
    );
  });
  return <>{groupedProducts}</>;
}

export default Products;
